/**
 * Custom room layout — replaces LiveKit's stock <VideoConference /> grid.
 *
 * Dominance rule:
 *   - If the field user is in the room → field's camera fills the main
 *     tile for EVERYONE. That's the inspection's "annotation surface" —
 *     the operator marks the field's video, so it has to be visible at
 *     full resolution on both sides.
 *   - Before the field joins (or if their camera drops) → the local
 *     participant's own camera fills the main tile so the operator
 *     sees themselves while they wait, instead of staring at an empty
 *     placeholder. The dominant tile swaps to the field automatically
 *     the moment they publish their camera.
 *   - Whoever isn't dominant becomes a thumbnail in the bottom strip.
 *
 * Field/operator discrimination: token identities are minted as
 *   `participant:<uuid>`  for field users (HMAC-token-redeemed)
 *   `user:<id>`           for operators / observers (JWT-authed)
 * Per AGENTS.md §27 1B-D-rec — don't change the prefix scheme; auth
 * + audit downstream rely on it.
 *
 * Media controls (mic / camera / screen-share / leave) live in a
 * hand-rolled <MediaControls /> at the bottom. We don't use LiveKit's
 * <ControlBar /> prefab because it permission-gates every button on a
 * `useLocalParticipantPermissions()` hook that races first-render and
 * silently empties the bar. See the MediaControls docstring below.
 *
 * Mirror handling: ParticipantTile does the right thing already —
 * local-camera + facingMode=user → CSS scaleX(-1) selfie mirror; rear
 * camera (facingMode=environment) → no mirror. The annotation overlay
 * reads the rendered video's computed transform and matches it.
 */
import {
  DisconnectButton,
  MediaDeviceMenu,
  ParticipantTile,
  StartAudio,
  TrackRefContext,
  TrackToggle,
  useLocalParticipant,
  useTracks,
} from "@livekit/components-react";
import { isTrackReference } from "@livekit/components-core";
import { Track } from "livekit-client";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { identityOf, pickDominantTrack } from "@/lib/roomDominance";
import { useParticipantRoleLookup } from "@/lib/useParticipantRoleLookup";

/**
 * Operator-only layout mode (Phase 1B-I, 2026-05-29).
 *
 *   - "spotlight"     — current default. One large dominant tile +
 *                       thumbnail strip below. Annotation tools target
 *                       the dominant tile.
 *   - "side-by-side"  — dominant + first other rendered 50/50 across
 *                       the viewport, remaining cameras as a small
 *                       thumbnail strip below. Annotation still targets
 *                       the dominant half (so the operator can mark up
 *                       the field while seeing themselves at parity).
 *   - "grid"          — equal CSS-grid tiles for every camera. No
 *                       dominant-tile data attribute is rendered, so
 *                       AnnotationOverlay falls through to its legacy
 *                       selector or no-ops — grid mode is for situational
 *                       awareness, not annotation.
 *
 * Persisted in localStorage under `nb.room.layoutMode` so the operator
 * doesn't re-pick every session. Field side ignores the mode entirely
 * and always renders spotlight (its UX overhaul lives in FieldLayout).
 */
export type LayoutMode = "spotlight" | "side-by-side" | "grid";

const LAYOUT_STORAGE_KEY = "nb.room.layoutMode";
const LAYOUT_MODES: LayoutMode[] = ["spotlight", "side-by-side", "grid"];

function readStoredLayoutMode(): LayoutMode {
  if (typeof window === "undefined") return "spotlight";
  try {
    const raw = window.localStorage.getItem(LAYOUT_STORAGE_KEY);
    if (raw && LAYOUT_MODES.includes(raw as LayoutMode)) {
      return raw as LayoutMode;
    }
  } catch {
    // Private-mode Safari throws on localStorage access — fall through.
  }
  return "spotlight";
}

type Props = {
  /** "operator" or "field" — drives the placeholder copy and the set
   *  of media controls shown in the bottom bar. The dominance rule
   *  itself is symmetric. */
  which: "operator" | "field";
  /** Phase 1B-H-multi (UX) — optional session id for the role-label
   *  lookup. When set, each tile renders a small role badge (Field /
   *  Observer / Auditor) so the operator can tell who's who at a
   *  glance. Omitted → tiles show identity only. */
  sessionId?: string;
  /** Rendered inside the floating operator control cluster, after a
   *  divider. SessionPage owns the end-session mutation, so it hands
   *  the button down rather than this file duplicating the call. */
  endSlot?: ReactNode;
};

export function RoomLayout({ which, sessionId, endSlot }: Props) {
  const { localParticipant } = useLocalParticipant();
  const isOperator = which === "operator";
  // Phase 1B-H-multi (UX) — operator can click a thumbnail to promote
  // it to the dominant tile (e.g. when an observer is demonstrating
  // something on their camera and the operator wants to focus on
  // them). Persists for the operator-side view only; the field side
  // ignores promotion entirely so all field users see the same
  // hierarchy (screen-share > field > local).
  const [promotedIdentity, setPromotedIdentity] = useState<string | null>(null);
  const [layoutMode, setLayoutMode] = useState<LayoutMode>(() =>
    isOperator ? readStoredLayoutMode() : "spotlight",
  );
  // Persist operator layout choice. Field side is hard-pinned to
  // spotlight so this is a no-op there. localStorage.setItem can throw
  // in private-mode Safari — swallow it; UX still works for the session.
  useEffect(() => {
    if (!isOperator) return;
    try {
      window.localStorage.setItem(LAYOUT_STORAGE_KEY, layoutMode);
    } catch {
      // ignore
    }
  }, [layoutMode, isOperator]);
  const roleLookup = useParticipantRoleLookup(sessionId);

  // `withPlaceholder: true` mirrors LiveKit's stock <VideoConference />.
  // It returns a track ref for the LOCAL participant even before they
  // publish a camera, which is what nudges the device subsystem to
  // run getUserMedia. With `withPlaceholder: false` we got back an
  // empty array, no tile was rendered, and the browser permission
  // prompt never appeared.
  //
  // We deliberately DO NOT pass `updateOnlyOn`: the default set of
  // events (TrackPublished / TrackSubscribed / TrackMuted / etc.)
  // ensures the hook re-renders the moment a published camera arrives,
  // so the placeholder ref gets swapped for the real track and the
  // <ParticipantTile> starts rendering actual video pixels. Restricting
  // to `[ActiveSpeakersChanged]` (as VideoConference does) silently
  // drops the publish event and the tile stays stuck on a placeholder.
  //
  // Screen-share is fetched separately so it stays out of the camera
  // dominance arithmetic until we promote it. No placeholder for
  // ScreenShare — a participant who isn't actively sharing shouldn't
  // produce a phantom screen-share tile.
  const cameraTracks = useTracks(
    [{ source: Track.Source.Camera, withPlaceholder: true }],
    { onlySubscribed: false },
  );
  const screenShareTracks = useTracks(
    [{ source: Track.Source.ScreenShare, withPlaceholder: false }],
    { onlySubscribed: false },
  );

  // Belt-and-braces: explicitly request camera + mic once the local
  // participant exists. <LiveKitRoom video audio> is documented to do
  // this automatically, but on first connect (especially mobile Safari
  // and on second-tab joins) the auto-enable can fail silently.
  //
  // CRITICAL: guard with a ref so this fires EXACTLY ONCE per mount.
  // If we let it re-run on every `localParticipant` reference change
  // (which happens when React reconciles around an overlay open/close
  // like the EvidenceAnnotator), `setCameraEnabled(true)` against an
  // already-published track can reset facingMode to the default
  // ("user" / front) — undoing the operator's `camera.switch` to
  // "environment" and leaving the recording with a black gap while
  // the device renegotiates. Symptom: field's back camera flips back
  // to front when the collab annotator closes; recording shows black
  // for the renegotiation window.
  const initRef = useRef(false);
  useEffect(() => {
    if (!localParticipant || initRef.current) return;
    initRef.current = true;
    void localParticipant.setCameraEnabled(true).catch(() => {});
    void localParticipant.setMicrophoneEnabled(true).catch(() => {});
  }, [localParticipant]);

  const { dominant, others } = useMemo(
    () =>
      pickDominantTrack({
        cameraTracks,
        screenShareTracks,
        promotedIdentity,
        isOperator,
      }),
    [cameraTracks, screenShareTracks, promotedIdentity, isOperator],
  );

  // If the operator promoted a tile but the participant subsequently
  // left (or screen-share took over), clear the promotion so re-pinning
  // can happen cleanly later. Compare against the resolved dominant —
  // if the helper returned a different track than our promoted one, the
  // promotion is stale.
  useEffect(() => {
    if (!promotedIdentity || !isOperator) return;
    if (!dominant || identityOf(dominant) !== promotedIdentity) {
      setPromotedIdentity(null);
    }
  }, [dominant, promotedIdentity, isOperator]);

  // Grid mode flattens dominant + others back into a single set so the
  // CSS grid renders every camera at equal weight. Skip placeholders so
  // an unpublished participant doesn't appear as a black square.
  const allCameraTracks = useMemo(
    () =>
      (dominant ? [dominant, ...others] : others).filter(isTrackReference),
    [dominant, others],
  );

  return (
    <div className="relative flex h-full flex-col bg-nb-bg">
      {isOperator && (
        <LayoutModeToggle
          mode={layoutMode}
          onChange={setLayoutMode}
        />
      )}
      {layoutMode === "grid" && isOperator ? (
        <GridArea
          tracks={allCameraTracks}
          roleLookup={roleLookup}
          which={which}
        />
      ) : layoutMode === "side-by-side" && isOperator ? (
        <SideBySideArea
          dominant={dominant}
          others={others}
          which={which}
          isOperator={isOperator}
          promotedIdentity={promotedIdentity}
          onUnpin={() => setPromotedIdentity(null)}
          onPromote={(id) => setPromotedIdentity(id)}
          roleLookup={roleLookup}
        />
      ) : (
        <SpotlightArea
          dominant={dominant}
          others={others}
          which={which}
          isOperator={isOperator}
          promotedIdentity={promotedIdentity}
          onUnpin={() => setPromotedIdentity(null)}
          onPromote={(id) => setPromotedIdentity(id)}
          roleLookup={roleLookup}
        />
      )}

      <MediaControls which={which} endSlot={endSlot} />
    </div>
  );
}

type SubAreaProps = {
  dominant: ReturnType<typeof pickDominantTrack>["dominant"];
  others: ReturnType<typeof pickDominantTrack>["others"];
  which: "operator" | "field";
  isOperator: boolean;
  promotedIdentity: string | null;
  onUnpin: () => void;
  onPromote: (identity: string) => void;
  roleLookup: ReturnType<typeof useParticipantRoleLookup>;
};

/** Spotlight (default) — current behaviour: dominant fills, others
 * become a thumbnail strip along the bottom. */
function SpotlightArea({
  dominant,
  others,
  which,
  isOperator,
  promotedIdentity,
  onUnpin,
  onPromote,
  roleLookup,
}: SubAreaProps) {
  const { t } = useTranslation();
  return (
    <>
      {/* Dominant tile. The annotation overlay positions itself over
          this video element via DOM selectors; rendering it here keeps
          the math simple regardless of viewport changes.

          The `[&_video]:!object-contain` arbitrary variant overrides
          LiveKit's default `object-fit: cover` for the video element
          inside the dominant tile. Cover crops the video to fill the
          tile, which on a portrait phone (9:16) shown in a landscape
          desktop tile (16:9) hides most of the field's frame and
          stretches what's visible across the entire viewport. With
          contain the video letterboxes — proper aspect ratio,
          centred — and the AnnotationOverlay's letterbox math
          (which assumes `contain`) lines up correctly. The smaller
          rendered SVG area also keeps per-pointer-event renders
          cheap and the live-draft feel snappy. */}
      <div
        className="relative flex-1 overflow-hidden [&_video]:!object-contain"
        data-cvp-role="dominant-tile"
      >
        {dominant ? (
          <TrackRefContext.Provider value={dominant}>
            <ParticipantTile trackRef={dominant} className="h-full w-full" />
            <TileRoleBadge
              identity={identityOf(dominant)}
              roleLookup={roleLookup}
              isLocal={dominant.participant.isLocal}
              placement="top-left"
            />
            {isOperator && promotedIdentity && (
              <button
                type="button"
                data-testid="dominant-tile-unpin"
                onClick={onUnpin}
                className="absolute right-3 top-3 rounded-md bg-black/60 px-2 py-1 text-xs text-white hover:bg-black/80"
                title={t("ui.roomLayout.unpinTitleSpotlight")}
              >
                {t("ui.roomLayout.unpin")}
              </button>
            )}
          </TrackRefContext.Provider>
        ) : (
          <Placeholder which={which} />
        )}
      </div>

      {others.length > 0 && (
        <div
          className="flex h-32 shrink-0 items-center gap-2 overflow-x-auto bg-nb-bg/95 px-3 py-2"
          data-cvp-role="thumbnail-strip"
        >
          {others.map((t) => (
            <ThumbnailTile
              key={`${t.participant.identity}-${t.publication?.trackSid ?? "no-pub"}`}
              track={t}
              isOperator={isOperator}
              onPromote={onPromote}
              roleLookup={roleLookup}
            />
          ))}
        </div>
      )}
    </>
  );
}

/** Side-by-side — dominant + first other share the top half 50/50, the
 * remainder land in a thumbnail strip below. Annotation still targets
 * the dominant half because that's where the field's camera lives. */
function SideBySideArea({
  dominant,
  others,
  which,
  isOperator,
  promotedIdentity,
  onUnpin,
  onPromote,
  roleLookup,
}: SubAreaProps) {
  const { t } = useTranslation();
  const secondary = others.find(isTrackReference) ?? null;
  const remainder = secondary
    ? others.filter((t) => t !== secondary)
    : others;
  return (
    <>
      <div className="flex flex-1 gap-1 overflow-hidden bg-nb-bg">
        <div
          className="relative flex-1 overflow-hidden [&_video]:!object-contain"
          data-cvp-role="dominant-tile"
        >
          {dominant ? (
            <TrackRefContext.Provider value={dominant}>
              <ParticipantTile trackRef={dominant} className="h-full w-full" />
              <TileRoleBadge
                identity={identityOf(dominant)}
                roleLookup={roleLookup}
                isLocal={dominant.participant.isLocal}
                placement="top-left"
              />
              {isOperator && promotedIdentity && (
                <button
                  type="button"
                  data-testid="dominant-tile-unpin"
                  onClick={onUnpin}
                  className="absolute right-3 top-3 rounded-md bg-black/60 px-2 py-1 text-xs text-white hover:bg-black/80"
                  title={t("ui.roomLayout.unpinTitle")}
                >
                  {t("ui.roomLayout.unpin")}
                </button>
              )}
            </TrackRefContext.Provider>
          ) : (
            <Placeholder which={which} />
          )}
        </div>
        <div className="relative flex-1 overflow-hidden border-l border-nb-border [&_video]:!object-contain">
          {secondary ? (
            <TrackRefContext.Provider value={secondary}>
              <ParticipantTile trackRef={secondary} className="h-full w-full" />
              <TileRoleBadge
                identity={identityOf(secondary)}
                roleLookup={roleLookup}
                isLocal={secondary.participant.isLocal}
                placement="top-left"
              />
            </TrackRefContext.Provider>
          ) : (
            <div className="flex h-full items-center justify-center text-sm text-nb-text-2">
              {t("ui.roomLayout.waitingForParticipant")}
            </div>
          )}
        </div>
      </div>

      {remainder.length > 0 && (
        <div
          className="flex h-28 shrink-0 items-center gap-2 overflow-x-auto bg-nb-bg/95 px-3 py-2"
          data-cvp-role="thumbnail-strip"
        >
          {remainder.map((t) => (
            <ThumbnailTile
              key={`${t.participant.identity}-${t.publication?.trackSid ?? "no-pub"}`}
              track={t}
              isOperator={isOperator}
              onPromote={onPromote}
              roleLookup={roleLookup}
            />
          ))}
        </div>
      )}
    </>
  );
}

/** Grid — all cameras at equal weight in a responsive CSS grid. No
 * dominant-tile marker is emitted, so the annotation overlay falls
 * through to its legacy selector or no-ops (the spec wants annotation
 * only on spotlight/side-by-side; grid is for situational awareness). */
function GridArea({
  tracks,
  roleLookup,
  which,
}: {
  tracks: ReturnType<typeof pickDominantTrack>["others"];
  roleLookup: ReturnType<typeof useParticipantRoleLookup>;
  which: "operator" | "field";
}) {
  if (tracks.length === 0) {
    return (
      <div className="relative flex flex-1 items-center justify-center bg-nb-bg">
        <Placeholder which={which} />
      </div>
    );
  }
  // Pick column count from track count — clamp 1..3 columns. Anything
  // above 9 tiles wraps to additional rows with the same column count.
  const cols = Math.min(3, Math.max(1, Math.ceil(Math.sqrt(tracks.length))));
  return (
    <div
      className="grid flex-1 gap-1 overflow-hidden bg-nb-bg p-1"
      style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}
      data-cvp-role="grid-area"
    >
      {tracks.map((t) => (
        <div
          key={`${t.participant.identity}-${t.publication?.trackSid ?? "no-pub"}`}
          className="relative overflow-hidden rounded-md bg-black [&_video]:!object-contain"
        >
          <TrackRefContext.Provider value={t}>
            <ParticipantTile trackRef={t} className="h-full w-full" />
            <TileRoleBadge
              identity={identityOf(t)}
              roleLookup={roleLookup}
              isLocal={t.participant.isLocal}
              placement="bottom-left"
            />
          </TrackRefContext.Provider>
        </div>
      ))}
    </div>
  );
}

function ThumbnailTile({
  track,
  isOperator,
  onPromote,
  roleLookup,
}: {
  track: ReturnType<typeof pickDominantTrack>["others"][number];
  isOperator: boolean;
  onPromote: (identity: string) => void;
  roleLookup: ReturnType<typeof useParticipantRoleLookup>;
}) {
  const { t } = useTranslation();
  const ident = identityOf(track);
  const clickable = isOperator && !!ident;
  const Wrapper = clickable ? "button" : "div";
  return (
    <Wrapper
      type={clickable ? "button" : undefined}
      onClick={clickable ? () => onPromote(ident) : undefined}
      data-testid={clickable ? "promote-thumb" : "static-thumb"}
      className={`relative aspect-video h-full shrink-0 overflow-hidden rounded-md border border-nb-border bg-black ${
        clickable ? "cursor-pointer transition hover:border-nb-mint" : ""
      }`}
      title={
        clickable ? t("ui.roomLayout.promoteThumbTitle") : undefined
      }
    >
      <TrackRefContext.Provider value={track}>
        <ParticipantTile trackRef={track} className="h-full w-full" />
        <TileRoleBadge
          identity={ident}
          roleLookup={roleLookup}
          isLocal={track.participant.isLocal}
          placement="bottom-left"
        />
      </TrackRefContext.Provider>
    </Wrapper>
  );
}

function LayoutModeToggle({
  mode,
  onChange,
}: {
  mode: LayoutMode;
  onChange: (m: LayoutMode) => void;
}) {
  const { t } = useTranslation();
  // Floats over the dominant tile in the top-right. Three small buttons
  // — keeps screen real estate for the camera feed itself. The cluster
  // mirrors the unpin button's styling for visual consistency.
  return (
    <div
      data-testid="layout-mode-toggle"
      className="pointer-events-auto absolute left-1/2 top-4 z-20 flex -translate-x-1/2 gap-1 rounded-md bg-black/60 p-1 text-xs text-white shadow-md"
    >
      {LAYOUT_MODES.map((m) => (
        <button
          key={m}
          type="button"
          onClick={() => onChange(m)}
          data-testid={`layout-mode-${m}`}
          aria-pressed={mode === m}
          title={
            m === "spotlight"
              ? t("ui.roomLayout.spotlightTitle")
              : m === "side-by-side"
                ? t("ui.roomLayout.sideBySideTitle")
                : t("ui.roomLayout.gridTitle")
          }
          className={`rounded px-2 py-1 transition ${
            mode === m
              ? "bg-nb-mint text-nb-bg"
              : "hover:bg-white/15"
          }`}
        >
          {m === "spotlight"
            ? t("ui.roomLayout.spotlight")
            : m === "side-by-side"
              ? t("ui.roomLayout.sideBySide")
              : t("ui.roomLayout.grid")}
        </button>
      ))}
    </div>
  );
}

/**
 * Hand-rolled control bar. We skipped LiveKit's <ControlBar /> prefab
 * because it gates EVERY button on `useLocalParticipantPermissions()`
 * returning a non-null value AND silently forces every control to
 * `false` until permissions arrive — which on first connect is a race
 * we lost more often than not, leaving the operator with an empty bar.
 *
 * These primitives (TrackToggle, MediaDeviceMenu, DisconnectButton,
 * StartAudio) are the building blocks ControlBar itself composes from;
 * we just compose them with no permission-based hiding so the buttons
 * appear immediately. The buttons themselves no-op gracefully if the
 * underlying device or permission isn't available.
 *
 * Each toggle pairs with its MediaDeviceMenu (the chevron dropdown to
 * switch input devices) using LiveKit's `lk-button-group` class so the
 * default theme's group styling works.
 */
// Exported for the placement test — the overlay positioning is the
// feature, so it is worth pinning.
export function MediaControls({
  which,
  endSlot,
}: {
  which: "operator" | "field";
  endSlot?: ReactNode;
}) {
  const { t } = useTranslation();
  const isOperator = which === "operator";
  return (
    <div
      className={
        isOperator
          ? // Floating over the video rather than a bar beneath it, so
            // the operator's own mic/camera/share sit with the picture
            // they affect. Stacks under OperatorToolbar's field-ops
            // pill (bottom-20) — these two must not overlap.
            "lk-control-bar pointer-events-auto absolute bottom-4 left-1/2 z-30 " +
            "flex w-auto -translate-x-1/2 items-center gap-1.5 rounded-full " +
            "border border-white/15 bg-black/55 px-3 py-2 shadow-2xl backdrop-blur-md"
          : "lk-control-bar shrink-0 border-t border-nb-border"
      }
      data-lk-theme="default"
      data-cvp-role="control-bar"
    >
      <div className="lk-button-group">
        <TrackToggle source={Track.Source.Microphone} showIcon>
          {t("ui.roomLayout.microphone")}
        </TrackToggle>
        <div className="lk-button-group-menu">
          <MediaDeviceMenu kind="audioinput" />
        </div>
      </div>
      <div className="lk-button-group">
        <TrackToggle source={Track.Source.Camera} showIcon>
          {t("ui.roomLayout.camera")}
        </TrackToggle>
        <div className="lk-button-group-menu">
          <MediaDeviceMenu kind="videoinput" />
        </div>
      </div>
      {which === "operator" && (
        <TrackToggle
          source={Track.Source.ScreenShare}
          showIcon
          captureOptions={{ audio: true, selfBrowserSurface: "include" }}
        >
          {t("ui.roomLayout.shareScreen")}
        </TrackToggle>
      )}
      {/* Only the FIELD technician gets a Leave (disconnect) button — the
          operator ends the whole session via the "End session" control
          (SessionPage), so a second LiveKit disconnect button on their side
          was just confusing (Leave keeps the session open). Field-tech
          disconnect auto-finalizes their participant egress (LiveKit ends the
          egress on participant disconnect → EGRESS_ENDED → ingested). */}
      {which === "field" && <DisconnectButton>{t("ui.roomLayout.leave")}</DisconnectButton>}
      <StartAudio label={t("ui.roomLayout.allowAudio")} />
      {/* Ending the session lived only in the far-right sidebar, nowhere
          near the call it ends. Rendered here behind a divider so it
          reads as separate from the toggles beside it — it is the one
          control with no undo. The sidebar button stays: this overlay
          exists only while the room is mounted, and a scheduled session
          nobody has joined still has to be endable. */}
      {endSlot && (
        <>
          <span
            aria-hidden="true"
            className="mx-1 h-6 w-px shrink-0 bg-white/20"
          />
          {endSlot}
        </>
      )}
    </div>
  );
}

function TileRoleBadge({
  identity,
  roleLookup,
  isLocal,
  placement,
}: {
  identity: string;
  roleLookup: ReturnType<typeof useParticipantRoleLookup>;
  isLocal: boolean;
  placement: "top-left" | "bottom-left";
}) {
  const { t } = useTranslation();
  // Identity scheme: `user:<id>` = operator (no Participant row),
  // `participant:<uuid>` = field/observer/auditor (looked up via the
  // sessions participants endpoint).
  let label: string | null = null;
  if (identity.startsWith("user:")) {
    label = t("ui.roomLayout.roleOperator");
  } else if (identity.startsWith("participant:")) {
    const uuid = identity.slice("participant:".length);
    const role = roleLookup.byParticipantId[uuid];
    if (role === "field") label = t("ui.roomLayout.roleField");
    else if (role === "observer") label = t("ui.roomLayout.roleObserver");
    else if (role === "auditor") label = t("ui.roomLayout.roleAuditor");
  }
  if (!label && isLocal) label = t("ui.roomLayout.roleYou");
  if (!label) return null;
  const placementClasses =
    placement === "top-left"
      ? "left-3 top-3"
      : "bottom-2 left-2";
  return (
    <span
      data-testid="tile-role-badge"
      className={`pointer-events-none absolute rounded-md bg-black/60 px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide text-white ${placementClasses}`}
    >
      {label}
    </span>
  );
}

function Placeholder({ which }: { which: "operator" | "field" }) {
  const { t } = useTranslation();
  return (
    <div className="flex h-full items-center justify-center bg-nb-surface/30">
      <p className="text-sm text-nb-text-2">
        {which === "operator"
          ? t("ui.roomLayout.placeholderOperator")
          : t("ui.roomLayout.placeholderField")}
      </p>
    </div>
  );
}
